/**
 * Engagement Quality — Phase 10c. Surfaces out_22b
 * (engagement_quality_by_federation): per-(federation, band) member counts
 * and component averages from the 6-weight composite score (reply intent
 * quality 30%, sentiment 20%, click+download depth 15%, survey/flow
 * completion 15%, face-time signal 10%, legacy-intent agreement 10%).
 * Internal CBO only.
 */

import React from 'react';
import { CheckCircle2, Activity } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useEngagementQuality, type EngagementQualityRow } from '@/hooks/ml';

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmt = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : Math.round(n).toLocaleString();
};

const numFmt = (v: unknown, digits = 1): string => {
  const n = num(v);
  return n == null ? '—' : n.toFixed(digits);
};

const BAND_TONE: Record<string, string> = {
  HIGH: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-300',
  LOW: 'bg-rose-100 text-rose-700 border-rose-200',
};

const BAND_RANK: Record<string, number> = { HIGH: 1, MEDIUM: 2, LOW: 3 };

interface FederationGroup {
  federation_code: string;
  rows: EngagementQualityRow[];
  total: number;
}

export const EngagementQualityCard: React.FC = () => {
  const q = useEngagementQuality();

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">Loading engagement quality…</CardContent>
      </Card>
    );
  }
  if (q.isError || !q.data?.rows.length) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">
          Engagement quality unavailable (internal CBO only).
        </CardContent>
      </Card>
    );
  }

  // Group by federation
  const groups: FederationGroup[] = [];
  const byFed = new Map<string, EngagementQualityRow[]>();
  for (const r of q.data.rows) {
    if (!byFed.has(r.federation_code)) byFed.set(r.federation_code, []);
    byFed.get(r.federation_code)!.push(r);
  }
  for (const [federation_code, rows] of byFed.entries()) {
    rows.sort((a, b) => (BAND_RANK[a.band] ?? 9) - (BAND_RANK[b.band] ?? 9));
    groups.push({
      federation_code,
      rows,
      total: rows.reduce((a, b) => a + (b.members ?? 0), 0),
    });
  }
  groups.sort((a, b) => b.total - a.total);

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-start gap-1.5 text-xs text-gray-600">
          <Activity className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-emerald-500" />
          <span>
            Per-member engagement-quality composite (out_22). Weights: 30%
            reply intent quality, 20% sentiment, 15% click+download depth,
            15% survey/flow completion, 10% face-time signal (appointment +
            progress notes + file upload), 10% legacy-intent agreement.
            Bands: HIGH ≥ 60, MEDIUM 30–59, LOW &lt; 30.
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="py-1.5 pr-3 font-medium">Federation</th>
                <th className="py-1.5 pr-3 font-medium text-center">Band</th>
                <th className="py-1.5 pr-3 font-medium text-right">Members</th>
                <th className="py-1.5 pr-3 font-medium text-right">Share</th>
                <th className="py-1.5 pr-3 font-medium text-right">Avg score</th>
                <th className="py-1.5 pr-3 font-medium text-right">Avg clicks</th>
                <th className="py-1.5 pr-3 font-medium text-right">Avg downloads</th>
                <th className="py-1.5 pr-3 font-medium text-right">Face-time signals</th>
                <th className="py-1.5 pr-3 font-medium text-right">Legacy-intent ✓</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) =>
                g.rows.map((r, idx) => (
                  <tr
                    key={`${g.federation_code}-${r.band}`}
                    className="border-b border-gray-100 hover:bg-gray-50/40"
                  >
                    {idx === 0 ? (
                      <td
                        rowSpan={g.rows.length}
                        className="py-2 pr-3 align-top"
                      >
                        <div className="font-semibold text-gray-900">{g.federation_code}</div>
                        <div className="text-[10px] text-gray-500">{fmt(g.total)} scored</div>
                      </td>
                    ) : null}
                    <td className="py-2 pr-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-semibold ${
                          BAND_TONE[r.band] ?? 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}
                      >
                        {r.band === 'HIGH' && <CheckCircle2 className="h-2.5 w-2.5" />}
                        {r.band}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmt(r.members)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-gray-600">
                      {g.total > 0 ? `${((r.members / g.total) * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{numFmt(r.avg_score, 1)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{numFmt(r.avg_clicks, 2)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {numFmt(r.avg_downloads, 2)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {numFmt(r.avg_face_time_signals, 2)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {fmt(r.members_legacy_matched)}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-gray-500 leading-relaxed">
          Face-time signals = count of (appointment booked, progress note written,
          file uploaded). Legacy-intent ✓ = members whose latest reply matched the
          legacy regex-based classifier — a data-quality cross-check that legacy
          and LLM classifiers agree.
        </div>
      </CardContent>
    </Card>
  );
};

export default EngagementQualityCard;
